// REPANCY-AGENT-LOCK: agent-ux phase-K.8 (until 2026-05-24)
/**
 * K.8 — Preview pane for a connected-site driveItem (K.7 Sites.Selected
 * grant). Mounts inside DocumentPreview when previewSource.kind is
 * 'remote-driveItem'. Calls the server-side
 * `userSelectedSitesPreviewNode.getSelectedSiteItemPreview` action which
 * runs in the Convex Node.js runtime (mammoth + turndown live there per
 * `convex/RUNTIME-STANDARD.md`).
 *
 * Renders by kind:
 *   - docx     → sanitized HTML via DOMPurify + 'Markdown' / 'Plain
 *                text' tab switcher.
 *   - markdown → raw markdown in <pre>.
 *   - text     → raw text in <pre>.
 *   - binary   → metadata + "Open in SharePoint" link only.
 *
 * Sanitization invariant: mammoth README warns that mammoth performs
 * no source sanitization. We pipe its HTML through DOMPurify before
 * `dangerouslySetInnerHTML`. Threat surface is small (user's own SP),
 * but the safe move is to sanitize regardless.
 */

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useAction, useQuery } from 'convex/react';
import DOMPurify from 'dompurify';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import {
  renderMarkdownToHtml,
  stripMarkdown,
} from '../../lib/sharepoint/markdownRender';
import { pushConnectedSitePermissionErrorToast } from '../../lib/graph/useGraphErrorToast';
import { saveBack } from '../../lib/graph/saveBack';
import { PreviewTipTapEditor } from './PreviewTipTapEditor';
import { useOptionalTenantBranding } from '../../lib/tenant/TenantBrandingContext';

interface RemoteDriveItemPreviewProps {
  siteId: string;
  driveId: string;
  itemId: string;
  name: string;
  webUrl: string | null;
}

type View = 'rendered' | 'markdown' | 'text';

type PreviewResult = Awaited<
  ReturnType<
    ReturnType<
      typeof useAction<
        typeof api.userSelectedSitesPreviewNode.getSelectedSiteItemPreview
      >
    >
  >
>;

const FONT = 'var(--xp-font-body)';

export function RemoteDriveItemPreview({
  siteId,
  driveId,
  itemId,
  name,
  webUrl,
}: RemoteDriveItemPreviewProps): JSX.Element {
  const fetchPreview = useAction(
    api.userSelectedSitesPreviewNode.getSelectedSiteItemPreview,
  );
  // F-AZURE-SEARCH Phase 2 — warm-on-read indexing. Fire-and-forget
  // after a successful preview load. The action does its own grant
  // check + 5-min debounce; no need to gate from the SPA.
  const indexExternalDriveItem = useAction(
    api.searchAdminExternal.indexExternalDriveItem,
  );
  const portal = useOptionalTenantBranding();
  const portalSlug = portal?.slug ?? null;
  // K.11.3 — fetch the current per-site permission so we can render
  // a read-only banner + inline Upgrade CTA. Routes to the K.6
  // lookupGrant query (per-siteId; returns the grant row or null).
  const grant = useQuery(api.userSelectedSites.lookupGrant, { siteId });
  const upgradeSitePermission = useAction(
    api.userSelectedSitesPermissionNode.upgradeSitePermission,
  );
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('rendered');
  // K.12.1 — edit state. `draft` is the textarea value; `baseline`
  // is what we last loaded from Graph so we can compute dirty.
  // `null` baseline means we haven't loaded a body yet (or the
  // file isn't editable).
  const [draft, setDraft] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<string | null>(null);
  // K.12.2 — save state. `saving` gates the Save button.
  // `saveError` holds the inline error panel content (K.12.3).
  // `saveSuccess` shows a transient confirmation banner.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ message: string; learnMoreUrl: string | null } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    setView('rendered');
    // K.12.1 — reset edit state on every new item.
    setDraft(null);
    setBaseline(null);
    setSaveError(null);
    setSaveSuccess(false);
    fetchPreview({ siteId, driveId, itemId })
      .then((r) => {
        if (cancelled) return;
        setResult(r);
        // K.12.1 — seed draft + baseline for editable kinds.
        if (r.ok === true && r.kind === 'markdown') {
          setDraft(r.markdown);
          setBaseline(r.markdown);
        } else if (r.ok === true && r.kind === 'text') {
          setDraft(r.text);
          setBaseline(r.text);
        }
        // F-AZURE-SEARCH Phase 2 — warm-on-read: schedule an opportunistic
        // re-index of this driveItem so a future Corpus Search finds it.
        // Fire-and-forget; the action handles auth, debouncing, and
        // binary skipping internally. Only runs when we have a portal
        // context (the connected-site tree mounts inside a portal).
        if (r.ok === true && portalSlug !== null) {
          void indexExternalDriveItem({
            clientSlug: portalSlug,
            siteId,
            driveId,
            itemId,
          }).catch(() => {
            // Never block the preview render; failures audit to
            // searchIndexQueueLog inside the action.
          });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, driveId, itemId, fetchPreview, indexExternalDriveItem, portalSlug]);

  // Sanitize the docx HTML before injecting. Memoized on the raw
  // html string so we don't re-sanitize on every render.
  const sanitizedHtml = useMemo(() => {
    if (result?.ok === true && result.kind === 'docx') {
      return DOMPurify.sanitize(result.html, {
        // Keep all the structural tags mammoth emits; drop on*= handlers
        // + javascript: links.
        USE_PROFILES: { html: true },
      });
    }
    return '';
  }, [result]);

  // K.10 — Render the .md branch through marked → DOMPurify so the
  // Rendered view of a markdown driveItem looks like the GitHub-style
  // render its author expected. marked emits HTML strings; we never
  // pass them to dangerouslySetInnerHTML without DOMPurify (same
  // contract as the docx branch).
  // K.12.1 — when canEdit, render the user's `draft` so the
  // Rendered + Plain views show what they're about to save. When
  // read-only (or kind !== 'markdown'), fall back to result.markdown.
  const sanitizedMarkdownHtml = useMemo(() => {
    if (result?.ok === true && result.kind === 'markdown') {
      const source = draft ?? result.markdown;
      const html = renderMarkdownToHtml(source);
      return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    }
    return '';
  }, [result, draft]);

  // K.10 — best-effort plain-text strip for the markdown branch.
  const strippedMarkdownText = useMemo(() => {
    if (result?.ok === true && result.kind === 'markdown') {
      return stripMarkdown(draft ?? result.markdown);
    }
    return '';
  }, [result, draft]);

  // K.11.3 — read-only banner + inline upgrade CTA.
  // Only renders when the grant exists AND permission is 'read'. If
  // the grant query is still loading (undefined) we render nothing —
  // avoids a flash of "read-only" before the row arrives.
  async function handleInlineUpgrade(): Promise<void> {
    setUpgradeError(null);
    setUpgrading(true);
    try {
      const r = await upgradeSitePermission({
        siteId,
        targetPermission: 'readwrite',
      });
      if (r.ok === false) {
        if (r.reason === 'graph-error') {
          // K.11.4 — toast carries learnMoreUrl; inline panel shows the
          // raw message + sends telemetry via the toast helper.
          pushConnectedSitePermissionErrorToast({
            message: `Couldn't upgrade '${name}' site permission. ${r.message}`,
            learnMoreUrl: r.learnMoreUrl,
          });
          setUpgradeError(r.message);
        } else {
          setUpgradeError(r.reason);
        }
      }
    } catch (err) {
      setUpgradeError(err instanceof Error ? err.message : String(err));
    } finally {
      setUpgrading(false);
    }
  }

  // K.12.1 — derived edit state.
  // `editorCapability` comes from the K.12.0 server-side meta.
  // `canEdit` = capability + write-permission + we have a baseline.
  // `isDirty` = the textarea diverges from the baseline.
  const editorCapability =
    result?.ok === true && result.kind !== 'binary'
      ? (result.meta as { editorCapability?: 'viewer' | 'editor' }).editorCapability ?? 'viewer'
      : 'viewer';
  const isReadWrite = grant !== null && grant !== undefined && grant.permission === 'readwrite';
  const canEdit = editorCapability === 'editor' && isReadWrite && baseline !== null;
  const isDirty = draft !== null && baseline !== null && draft !== baseline;

  // K.12.2 — save handler. Uses the delegated `saveBack` path
  // (Files.ReadWrite.All in MSAL) — the user's token, not the
  // server-side app-only token, because PUT /drives/.../content
  // works with the delegated write scope; only permission PATCH
  // requires app-only.
  const handleSave = useCallback(async (): Promise<void> => {
    if (!canEdit || !isDirty || draft === null) return;
    if (result?.ok !== true) return;
    const meta = result.meta as {
      cTag: string | null;
      eTag: string | null;
      webUrl: string | null;
      name: string;
    };
    const etag = meta.cTag ?? meta.eTag ?? '';
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const r = await saveBack(
        {
          siteId,
          driveId,
          itemId,
          etag,
          webUrl: meta.webUrl ?? '',
        },
        draft,
      );
      if (r.kind === 'ok') {
        setBaseline(draft);
        setSaveSuccess(true);
        // K.12.3 — auto-clear the success badge after a short pause.
        setTimeout(() => setSaveSuccess(false), 3000);
      } else if (r.kind === 'conflict') {
        // K.12.4 — dispatch the F3 ConflictDialogHost event with the
        // shape the host expects (externalRef + localContent + remoteEtag).
        // The host renders ConflictDialog; user picks overwrite /
        // keep-theirs / save-as-new-local; resolveConflict() runs.
        // On 'overwrite' success the host emits
        // `repancy:save-back-conflict-resolved` which we listen for
        // below to flip our baseline.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('repancy:save-back-conflict', {
              detail: {
                externalRef: {
                  siteId,
                  driveId,
                  itemId,
                  etag,
                  webUrl: meta.webUrl ?? '',
                },
                localContent: draft,
                remoteEtag: r.remoteEtag,
                documentTitle: meta.name,
              },
            }),
          );
        }
        setSaveError({
          message:
            'Someone else edited this file since you opened it. Choose Overwrite to keep yours, Keep theirs to reload, or Save-as-new-local.',
          learnMoreUrl: null,
        });
      } else {
        // r.kind === 'error'
        setSaveError({
          message: `Save failed: ${r.message}`,
          learnMoreUrl:
            /403/.test(r.message)
              ? 'https://learn.microsoft.com/en-us/sharepoint/dev/solution-guidance/security-apponly-azuread'
              : /423/.test(r.message)
                ? 'https://learn.microsoft.com/en-us/purview/sensitivity-labels'
                : null,
        });
      }
    } catch (err) {
      setSaveError({
        message: err instanceof Error ? err.message : String(err),
        learnMoreUrl: null,
      });
    } finally {
      setSaving(false);
    }
  }, [canEdit, isDirty, draft, result, siteId, driveId, itemId]);

  // K.12.1 — discard draft → reset to baseline.
  const handleDiscard = useCallback((): void => {
    if (baseline !== null) {
      setDraft(baseline);
      setSaveError(null);
    }
  }, [baseline]);

  // K.12.4 — listen for ConflictDialogHost's resolution. If the user
  // picked 'overwrite' and Graph accepted it, flip our baseline so
  // isDirty resolves to false and the Save button greys out. If they
  // picked 'keep-theirs' or 'save-as-new-local' we leave the user's
  // draft in place — they decide what to do with it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (ev: Event) => {
      const detail = (
        ev as CustomEvent<{
          externalRef: { driveId: string; itemId: string };
          choice: 'overwrite' | 'keep-theirs' | 'save-as-new-local';
          result: { kind: string };
        }>
      ).detail;
      if (!detail) return;
      // Only react to resolutions targeting OUR item.
      if (
        detail.externalRef.driveId !== driveId ||
        detail.externalRef.itemId !== itemId
      ) {
        return;
      }
      if (detail.choice === 'overwrite' && detail.result.kind === 'ok') {
        // Our local content won. Mark clean + clear the error panel.
        setBaseline(draft);
        setSaveError(null);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    };
    window.addEventListener('repancy:save-back-conflict-resolved', handler);
    return () => {
      window.removeEventListener('repancy:save-back-conflict-resolved', handler);
    };
  }, [driveId, itemId, draft]);

  const permissionBannerOnly: JSX.Element | null =
    grant === undefined || grant === null
      ? null
      : grant.permission === 'read'
        ? (
            <div
              data-testid={testIds.explorer.remoteDriveItemReadOnlyBanner}
              style={{
                padding: '6px 10px',
                background: '#fff6c0',
                borderBottom: '1px solid #d4b85c',
                fontSize: 11,
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
              }}
            >
              <span aria-hidden="true">⚠</span>
              <span>
                This site is read-only. Upgrade to enable saving edits back to
                SharePoint.
              </span>
              <button
                type="button"
                data-testid={testIds.explorer.remoteDriveItemUpgradeInlineButton}
                disabled={upgrading}
                onClick={() => void handleInlineUpgrade()}
                style={{
                  marginLeft: 'auto',
                  border: '1px solid #404080',
                  background: '#d0d0f0',
                  color: '#000080',
                  padding: '2px 8px',
                  fontSize: 11,
                  cursor: upgrading ? 'wait' : 'pointer',
                  opacity: upgrading ? 0.6 : 1,
                }}
                title="Upgrade this site to read-write (lasts until you downgrade or revoke)."
              >
                {upgrading ? 'Upgrading…' : 'Upgrade to read-write'}
              </button>
              {upgradeError !== null ? (
                <span style={{ marginLeft: 8, color: '#a00', fontSize: 10 }}>
                  {upgradeError}
                </span>
              ) : null}
            </div>
          )
        : (
            <div
              data-testid={testIds.explorer.remoteDriveItemPermissionBadge}
              style={{
                padding: '4px 10px',
                background: '#eaf6ea',
                borderBottom: '1px solid #a0c0a0',
                fontSize: 10,
                color: '#0a5a0a',
                flexShrink: 0,
              }}
            >
              ✓ Read-write — Repancy may save edits to this site.
            </div>
          );

  // K.12.2/3 — Save toolbar (Save / Discard / dirty marker) + error
  // panel rendered persistently above the view switcher. Mounts only
  // when the file is editable AND the user has readwrite. Save error
  // is sticky until user successfully retries or discards.
  const editorToolbar: JSX.Element | null = canEdit
    ? (
        <div
          style={{
            padding: '4px 10px',
            background: '#ece9d8',
            borderBottom: '1px solid #aca899',
            fontSize: 11,
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            data-testid={testIds.explorer.remoteDriveItemSaveButton}
            disabled={!isDirty || saving}
            onClick={() => void handleSave()}
            style={{
              border: '1px solid #404080',
              background: isDirty && !saving ? '#d0d0f0' : '#e8e8e8',
              color: isDirty && !saving ? '#000080' : '#888',
              padding: '2px 8px',
              fontSize: 11,
              cursor: !isDirty || saving ? 'default' : 'pointer',
            }}
            title={
              !isDirty
                ? 'No unsaved changes'
                : saving
                  ? 'Saving…'
                  : 'Save changes back to SharePoint'
            }
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            data-testid={testIds.explorer.remoteDriveItemDiscardButton}
            disabled={!isDirty || saving}
            onClick={handleDiscard}
            style={{
              border: '1px solid #808080',
              background: '#f0f0f0',
              color: '#000',
              padding: '2px 8px',
              fontSize: 11,
              cursor: !isDirty || saving ? 'default' : 'pointer',
              opacity: !isDirty || saving ? 0.6 : 1,
            }}
            title="Discard your changes and restore the SharePoint version."
          >
            Discard
          </button>
          {isDirty ? (
            <span style={{ color: '#5a3e00', fontSize: 10 }}>
              ● Unsaved changes
            </span>
          ) : null}
          {saveSuccess ? (
            <span
              data-testid={testIds.explorer.remoteDriveItemSaveSuccessMessage}
              style={{ marginLeft: 'auto', color: '#0a5a0a', fontSize: 10 }}
            >
              ✓ Saved to SharePoint
            </span>
          ) : null}
        </div>
      )
    : null;

  // Connected-sites docx files save via the delegated `saveBack` path,
  // which PUTs the markdown body — sidecars (comments, tracked changes,
  // footnotes, images) do NOT survive the round-trip on this surface.
  // Auto-provisioned portal docx files use the server-side
  // `saveAutoProvisionedDocxItem` path which rebuilds the OOXML zip via
  // `exportDocxForDocument`. Tracked as F-DOCX-EDIT for connected sites.
  const looksDocx = name.toLowerCase().endsWith('.docx');
  const docxLossyBadge: JSX.Element | null = canEdit && looksDocx
    ? (
        <div
          data-testid={testIds.explorer.remoteDriveItemDocxLossyBadge}
          style={{
            padding: '4px 10px',
            background: '#fff8d6',
            borderBottom: '1px solid #c8a200',
            fontSize: 11,
            color: '#5a3e00',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
          title="Connected-site docx editing uses the delegated save path. The portal workspace preserves full Word fidelity."
        >
          <span aria-hidden="true">⚠</span>
          <span>
            <strong>Word fidelity not preserved on connected sites.</strong>{' '}
            Saving overwrites the .docx with markdown — comments, tracked
            changes, and footnotes will be lost.
          </span>
        </div>
      )
    : null;

  const saveErrorPanel: JSX.Element | null = saveError !== null
    ? (
        <div
          data-testid={testIds.explorer.remoteDriveItemSaveErrorPanel}
          style={{
            padding: '6px 10px',
            background: '#ffe8e8',
            borderBottom: '1px solid #cc6666',
            fontSize: 11,
            color: '#660000',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">⚠</span>
          <span>{saveError.message}</span>
          {saveError.learnMoreUrl !== null ? (
            <a
              href={saveError.learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: 4, color: '#0000a0', fontSize: 11 }}
            >
              Learn more ↗
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => setSaveError(null)}
            style={{
              marginLeft: 'auto',
              border: '1px solid #a04040',
              background: 'transparent',
              color: '#660000',
              padding: '0 6px',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      )
    : null;

  // Compose the four banners. Order: permission status → docx lossy
  // warning → editor toolbar → save error panel.
  const permissionBanner: JSX.Element | null =
    permissionBannerOnly || docxLossyBadge || editorToolbar || saveErrorPanel ? (
      <>
        {permissionBannerOnly}
        {docxLossyBadge}
        {editorToolbar}
        {saveErrorPanel}
      </>
    ) : null;

  if (loading) {
    return (
      <PreviewFrame name={name} webUrl={webUrl} topBanner={permissionBanner}>
        <div
          data-testid={testIds.explorer.remoteDriveItemLoading}
          style={{ padding: 12, fontFamily: FONT, color: '#5a3e00' }}
        >
          Loading preview…
        </div>
      </PreviewFrame>
    );
  }

  if (error !== null) {
    return (
      <PreviewFrame name={name} webUrl={webUrl} topBanner={permissionBanner}>
        <div
          data-testid={testIds.explorer.remoteDriveItemError}
          style={{ padding: 12, fontFamily: FONT, color: '#a00' }}
        >
          {error}
        </div>
      </PreviewFrame>
    );
  }

  if (result === null) {
    return <PreviewFrame name={name} webUrl={webUrl} topBanner={permissionBanner} />;
  }

  if (result.ok === false) {
    return (
      <PreviewFrame name={name} webUrl={webUrl} topBanner={permissionBanner}>
        <div
          data-testid={testIds.explorer.remoteDriveItemError}
          style={{ padding: 12, fontFamily: FONT, color: '#a00' }}
        >
          {reasonMessage(result.reason, result)}
        </div>
      </PreviewFrame>
    );
  }

  if (result.kind === 'binary') {
    return (
      <PreviewFrame name={name} webUrl={result.meta.webUrl ?? webUrl} topBanner={permissionBanner}>
        <div
          data-testid={testIds.explorer.remoteDriveItemBinary}
          style={{ padding: 12, fontFamily: FONT, fontSize: 12, color: '#000' }}
        >
          <p>
            This file type ({result.meta.mimeType ?? 'unknown'}) can&apos;t
            be previewed inline.
          </p>
          {result.meta.webUrl !== null ? (
            <p>
              <a
                data-testid={testIds.explorer.remoteDriveItemOpenInSharePoint}
                href={result.meta.webUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in SharePoint ↗
              </a>
            </p>
          ) : null}
        </div>
      </PreviewFrame>
    );
  }

  if (result.kind === 'docx') {
    return (
      <PreviewFrame
        name={result.meta.name}
        webUrl={result.meta.webUrl ?? webUrl}
        topBanner={permissionBanner}
      >
        <ViewSwitcher view={view} setView={setView} kinds={['rendered', 'markdown', 'text']} />
        {view === 'rendered' ? (
          <div
            data-testid={testIds.explorer.remoteDriveItemRendered}
            style={{
              padding: 12,
              fontFamily: FONT,
              fontSize: 12,
              background: '#fff',
              overflow: 'auto',
              flex: 1,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : view === 'markdown' ? (
          <pre
            data-testid={testIds.explorer.remoteDriveItemMarkdown}
            style={{
              padding: 12,
              margin: 0,
              fontFamily: 'var(--xp-font-mono, Consolas, monospace)',
              fontSize: 11,
              background: '#fff',
              overflow: 'auto',
              flex: 1,
              whiteSpace: 'pre-wrap',
            }}
          >
            {result.markdown}
          </pre>
        ) : (
          <pre
            data-testid={testIds.explorer.remoteDriveItemText}
            style={{
              padding: 12,
              margin: 0,
              fontFamily: 'var(--xp-font-mono, Consolas, monospace)',
              fontSize: 11,
              background: '#fff',
              overflow: 'auto',
              flex: 1,
              whiteSpace: 'pre-wrap',
            }}
          >
            {result.rawText}
          </pre>
        )}
      </PreviewFrame>
    );
  }

  if (result.kind === 'markdown') {
    // K.10 — 3-view switcher parity with the docx branch.
    // Rendered = marked → DOMPurify-sanitized HTML.
    // Markdown = raw markdown source.
    // Plain text = best-effort strip via stripMarkdown.
    return (
      <PreviewFrame
        name={result.meta.name}
        webUrl={result.meta.webUrl ?? webUrl}
        topBanner={permissionBanner}
      >
        <ViewSwitcher view={view} setView={setView} kinds={['rendered', 'markdown', 'text']} />
        {view === 'rendered' ? (
          <div
            data-testid={testIds.explorer.remoteDriveItemRendered}
            style={{
              padding: 12,
              fontFamily: FONT,
              fontSize: 12,
              background: '#fff',
              overflow: 'auto',
              flex: 1,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedMarkdownHtml }}
          />
        ) : view === 'markdown' ? (
          canEdit && draft !== null ? (
            <PreviewTipTapEditor value={draft} onChange={setDraft} />
          ) : (
            <pre
              data-testid={testIds.explorer.remoteDriveItemMarkdown}
              style={{
                padding: 12,
                margin: 0,
                fontFamily: 'var(--xp-font-mono, Consolas, monospace)',
                fontSize: 11,
                background: '#fff',
                overflow: 'auto',
                flex: 1,
                whiteSpace: 'pre-wrap',
              }}
            >
              {result.markdown}
            </pre>
          )
        ) : (
          <pre
            data-testid={testIds.explorer.remoteDriveItemText}
            style={{
              padding: 12,
              margin: 0,
              fontFamily: 'var(--xp-font-mono, Consolas, monospace)',
              fontSize: 11,
              background: '#fff',
              overflow: 'auto',
              flex: 1,
              whiteSpace: 'pre-wrap',
            }}
          >
            {strippedMarkdownText}
          </pre>
        )}
      </PreviewFrame>
    );
  }

  // result.kind === 'text'
  return (
    <PreviewFrame
      name={result.meta.name}
      webUrl={result.meta.webUrl ?? webUrl}
      topBanner={permissionBanner}
    >
      {canEdit && draft !== null ? (
        <textarea
          data-testid={testIds.explorer.remoteDriveItemEditor}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
          style={{
            padding: 12,
            margin: 0,
            fontFamily: 'var(--xp-font-mono, Consolas, monospace)',
            fontSize: 11,
            background: '#fff',
            color: '#000',
            border: 'none',
            outline: 'none',
            resize: 'none',
            overflow: 'auto',
            flex: 1,
            width: '100%',
            whiteSpace: 'pre-wrap',
            boxSizing: 'border-box',
          }}
        />
      ) : (
        <pre
          data-testid={testIds.explorer.remoteDriveItemText}
          style={{
            padding: 12,
            margin: 0,
            fontFamily: 'var(--xp-font-mono, Consolas, monospace)',
            fontSize: 11,
            background: '#fff',
            overflow: 'auto',
            flex: 1,
            whiteSpace: 'pre-wrap',
          }}
        >
          {result.text}
        </pre>
      )}
    </PreviewFrame>
  );
}

function PreviewFrame({
  name,
  webUrl,
  topBanner,
  children,
}: {
  name: string;
  webUrl: string | null;
  topBanner?: React.ReactNode;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div
      data-testid={testIds.explorer.remoteDriveItemRoot}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#f0f0f0',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          background: '#ece9d8',
          borderBottom: '1px solid #808080',
          flexShrink: 0,
          gap: 8,
        }}
      >
        <span
          data-testid={testIds.explorer.previewTitle}
          style={{ fontWeight: 'bold', fontSize: 13 }}
        >
          {name}
        </span>
        {webUrl !== null ? (
          <a
            data-testid={testIds.explorer.openInSharePoint}
            href={webUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11 }}
          >
            Open in SharePoint ↗
          </a>
        ) : null}
      </div>
      {topBanner ?? null}
      {children}
    </div>
  );
}

function ViewSwitcher({
  view,
  setView,
  kinds,
}: {
  view: View;
  setView: (v: View) => void;
  kinds: View[];
}): JSX.Element {
  return (
    <div
      data-testid={testIds.explorer.remoteDriveItemViewSwitcher}
      style={{
        display: 'flex',
        gap: 2,
        padding: '2px 4px',
        background: '#ece9d8',
        borderBottom: '1px solid #aca899',
        flexShrink: 0,
      }}
    >
      {kinds.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => setView(k)}
          data-testid={testIds.explorer.remoteDriveItemViewButton(k)}
          style={{
            padding: '2px 8px',
            fontSize: 11,
            fontFamily: FONT,
            background:
              view === k ? '#fff' : 'transparent',
            border:
              view === k
                ? '1px solid #316ac5'
                : '1px solid transparent',
            cursor: 'pointer',
          }}
        >
          {k === 'rendered' ? 'Rendered' : k === 'markdown' ? 'Markdown' : 'Plain text'}
        </button>
      ))}
    </div>
  );
}

function reasonMessage(
  reason: string,
  result: Extract<PreviewResult, { ok: false }>,
): string {
  switch (reason) {
    case 'not-authenticated':
      return 'Sign in to load this document.';
    case 'site-not-granted':
      return 'Access to this site was revoked.';
    case 'no-app-only-token':
      return 'Server-side Microsoft token unavailable.';
    case 'item-not-found':
      return 'This file is no longer in SharePoint.';
    case 'too-large':
      return `File too large to preview (${
        'size' in result
          ? `${Math.round(result.size / 1024)} KB`
          : 'unknown size'
      }).`;
    case 'graph-error':
    default:
      return `Could not load preview: ${
        'message' in result ? result.message : 'unknown error'
      }`;
  }
}
